import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row } from '../_fitnessHelpers';
import { FullPregnancyTable } from './_pregnancyHelpers';

const fmt = d => d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

/*
   Multiple calculation modes: Due Date | Last Period | Ultrasound | Conception | IVF
*/
export default function PregnancyCalc() {
  const [method, setMethod] = useState('lmp'); // lmp, ultrasound, conception, due, ivf
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [cycle, setCycle] = useState(28);
  const [gaWeeks, setGaWeeks] = useState(8);
  const [gaDays, setGaDays] = useState(0);
  const [ivfDay, setIvfDay] = useState(5);
  const [preWeight, setPreWeight] = useState(140); // Weight Before Pregnancy (lb)
  const [heightIn, setHeightIn] = useState(65);
  const clear = () => {
    setMethod('lmp'); setDate(new Date().toISOString().slice(0,10));
    setCycle(28); setGaWeeks(8); setGaDays(0); setIvfDay(5); setPreWeight(140); setHeightIn(65);
  };

  const c = useMemo(() => {
    const d = new Date(date); if (isNaN(d)) return null;
    let dueDate;
    if (method === 'lmp') {
      dueDate = new Date(d.getTime() + (280 + (cycle - 28)) * 86400000);
    } else if (method === 'conception') {
      dueDate = new Date(d.getTime() + 266 * 86400000);
    } else if (method === 'due') {
      dueDate = d;
    } else if (method === 'ultrasound') {
      // Adjust — get expected LMP from GA then + 280
      const daysSinceLMP = gaWeeks * 7 + gaDays;
      const lmp = new Date(d.getTime() - daysSinceLMP * 86400000);
      dueDate = new Date(lmp.getTime() + 280 * 86400000);
    } else { // ivf
      dueDate = new Date(d.getTime() + (266 - ivfDay + 14) * 86400000);
    }
    const today = new Date(); today.setHours(0,0,0,0);
    const lmp = new Date(dueDate.getTime() - 280 * 86400000);
    const conception = new Date(dueDate.getTime() - 266 * 86400000);
    const daysPregnant = Math.floor((today - lmp) / 86400000);
    const weeks = Math.floor(daysPregnant / 7);
    const days = daysPregnant % 7;
    const trimester = weeks < 13 ? '1st' : weeks < 27 ? '2nd' : '3rd';
    const firstTriEnd = new Date(lmp.getTime() + 12 * 7 * 86400000);
    const secondTriEnd = new Date(lmp.getTime() + 26 * 7 * 86400000);
    return { dueDate, lmp, conception, weeks, days, trimester, firstTriEnd, secondTriEnd };
  }, [method, date, cycle, gaWeeks, gaDays, ivfDay]);

  return (
    <CalcShell look="cnet" title="Pregnancy Calculator" subtitle="Estimate a pregnancy schedule from due date, last period, ultrasound, conception, or IVF transfer date.">
      <CalcNetPanel
        slug="preg"
        tabs={['Pregnancy']}
        activeTab={0}
        onTabChange={() => {}}
        onClear={clear}
        right={c && (
          <div>
            <div className="text-lg text-slate-800">Estimated due date</div>
            <div className="text-xl font-bold text-[#2f9c3f]" data-testid="preg-due">{fmt(c.dueDate)}</div>
            <table className="w-full text-sm border-collapse mt-4" data-testid="preg-schedule">
              <tbody>
                <tr><td className="border px-3 py-1.5">Gestational age today</td><td className="border px-3 py-1.5 text-right">{c.weeks} weeks {c.days} days</td></tr>
                <tr className="bg-slate-50"><td className="border px-3 py-1.5">Trimester</td><td className="border px-3 py-1.5 text-right">{c.trimester}</td></tr>
                <tr><td className="border px-3 py-1.5">First day of last period (LMP)</td><td className="border px-3 py-1.5 text-right">{fmt(c.lmp)}</td></tr>
                <tr className="bg-slate-50"><td className="border px-3 py-1.5">Conception date</td><td className="border px-3 py-1.5 text-right">{fmt(c.conception)}</td></tr>
                <tr><td className="border px-3 py-1.5">End of first trimester (12 wk)</td><td className="border px-3 py-1.5 text-right">{fmt(c.firstTriEnd)}</td></tr>
                <tr className="bg-slate-50"><td className="border px-3 py-1.5">End of second trimester (26 wk)</td><td className="border px-3 py-1.5 text-right">{fmt(c.secondTriEnd)}</td></tr>
              </tbody>
            </table>
            {/* Weight-gain recommendation table based on pre-pregnancy BMI (IOM guidelines) */}
            {(() => {
              const heightM = heightIn * 0.0254;
              const preKg = preWeight * 0.453592;
              const bmi = preKg / (heightM * heightM);
              let cat, minGain, maxGain;
              if (bmi < 18.5) { cat = 'Underweight'; minGain = 28; maxGain = 40; }
              else if (bmi < 25) { cat = 'Normal weight'; minGain = 25; maxGain = 35; }
              else if (bmi < 30) { cat = 'Overweight'; minGain = 15; maxGain = 25; }
              else { cat = 'Obese'; minGain = 11; maxGain = 20; }
              const wksNow = c.weeks;
              return (
                <div className="mt-4">
                  <h4 className="font-bold text-slate-900 mb-2 text-sm">Weekly Weight Gain (IOM guidelines)</h4>
                  <div className="text-xs mb-2">Pre-pregnancy BMI: <b>{bmi.toFixed(1)}</b> ({cat}) → Recommended total gain: <b>{minGain}-{maxGain} lb</b></div>
                  <table className="w-full text-xs border border-slate-300" data-testid="preg-weekly-table">
                    <thead className="bg-[#1e40af] text-white"><tr><th className="px-2 py-1.5 text-left">Week</th><th className="px-2 py-1.5 text-left">Trimester</th><th className="px-2 py-1.5 text-right">Expected Weight Gain (lb)</th><th className="px-2 py-1.5 text-right">Estimated Weight</th></tr></thead>
                    <tbody>
                      {Array.from({ length: 40 }, (_, i) => i + 1).filter(w => w % 4 === 0 || w === 1).map((w, idx) => {
                        const isTri1 = w <= 12;
                        const gain = isTri1 ? (w / 12) * 4 : 4 + ((w - 12) / (40 - 12)) * (((minGain + maxGain) / 2) - 4);
                        return (
                          <tr key={w} className={`${idx % 2 ? 'bg-slate-50' : ''} ${w === wksNow ? 'bg-emerald-100 font-bold' : ''}`}>
                            <td className="px-2 py-1">Week {w}{w === wksNow ? ' (current)' : ''}</td>
                            <td className="px-2 py-1">{w < 13 ? '1st' : w < 27 ? '2nd' : '3rd'}</td>
                            <td className="px-2 py-1 text-right font-mono">+{gain.toFixed(1)} lb</td>
                            <td className="px-2 py-1 text-right font-mono">{(preWeight + gain).toFixed(1)} lb</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            <FullPregnancyTable dueDate={c.dueDate} currentWeek={c.weeks} testId="preg-full-table" />
          </div>
        )}
      >
        <Row label="Calculate Based On">
          <select value={method} onChange={e => setMethod(e.target.value)} data-testid="preg-method"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            <option value="due">Due Date</option>
            <option value="lmp">Last Period (LMP)</option>
            <option value="ultrasound">Ultrasound Date</option>
            <option value="conception">Conception Date</option>
            <option value="ivf">IVF Transfer Date</option>
          </select>
        </Row>
        <Row label={
          method === 'due' ? 'Your Due Date' :
          method === 'lmp' ? 'First Day of LMP' :
          method === 'ultrasound' ? 'Ultrasound Date' :
          method === 'conception' ? 'Conception Date' : 'IVF Transfer Date'
        }>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="preg-date"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
        </Row>
        {method === 'lmp' && (
          <Row label="Cycle length">
            <input type="number" value={cycle} onChange={e => setCycle(+e.target.value || 0)} data-testid="preg-cycle"
              className="w-24 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
            <span className="text-slate-500 text-sm">days</span>
          </Row>
        )}
        {method === 'ultrasound' && (
          <Row label="Gestational age at ultrasound">
            <input type="number" value={gaWeeks} onChange={e => setGaWeeks(+e.target.value || 0)} data-testid="preg-gaw" className="w-20 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
            <span className="text-slate-500 text-sm">weeks</span>
            <input type="number" value={gaDays} onChange={e => setGaDays(+e.target.value || 0)} data-testid="preg-gad" className="w-20 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
            <span className="text-slate-500 text-sm">days</span>
          </Row>
        )}
        {method === 'ivf' && (
          <Row label="Embryo age at transfer">
            <select value={ivfDay} onChange={e => setIvfDay(+e.target.value)} data-testid="preg-ivf"
              className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
              <option value={3}>Day 3</option>
              <option value={5}>Day 5</option>
              <option value={6}>Day 6</option>
            </select>
          </Row>
        )}
        <Row label="Your Weight Before Pregnancy">
          <input type="number" value={preWeight} onChange={e => setPreWeight(+e.target.value || 0)} data-testid="preg-pre-weight"
            className="w-24 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
          <span className="text-slate-500 text-sm">pounds</span>
        </Row>
        <Row label="Your Height">
          <input type="number" value={heightIn} onChange={e => setHeightIn(+e.target.value || 0)} data-testid="preg-height"
            className="w-24 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
          <span className="text-slate-500 text-sm">inches</span>
        </Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Naegele's rule: due date ≈ LMP + 280 days (adjusted ±(cycle − 28))`,
        `Method chosen: ${method.toUpperCase()} — computed due date: <b>${c ? fmt(c.dueDate) : '—'}</b>`,
      ]} />
    </CalcShell>
  );
}
